import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaMusic, FaUser, FaGraduationCap } from 'react-icons/fa';
import initScrollAnimations from '../utils/scrollAnimation';
import EventCalendar from '../components/calendar/EventCalendar';
import { toCoverPosition } from '../utils/coverFocalPoint';
import { useSettings } from '../context/SettingsContext';
import AdminEditableSection from '../components/admin/AdminEditableSection';
import DeferredPageText from '../components/common/DeferredPageText';
import FeaturedVideos from '../components/home/FeaturedVideos';

// Default profile data to use if none exists
const defaultProfile = {
  name: 'Your Name',
  title: 'Musician • Performer • Teacher',
  shortBio: 'Professional musician with experience in performance and teaching',
  profileImage: '/images/default-profile.jpg',
  coverImage: '',
  coverVideo: ''
};

const Home = () => {
  const { pageContent, settings } = useSettings();
  const [profile, setProfile] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const coverVideoRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';
  const uploadsUrl = process.env.REACT_APP_UPLOADS_URL || '/uploads';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/profile`);
        if (res.data) {
          setProfile(res.data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setLoading(false);
      }
    };

    fetchProfile();
  }, [apiUrl]);

  // Initialize scroll animations on mount and after profile updates
  useEffect(() => {
    initScrollAnimations();
  }, []);
  useEffect(() => {
    initScrollAnimations();
  }, [profile]);

  // Prepare profile image URL
  const profileImageUrl = profile?.profileImage && !profile.profileImage.startsWith('/images') 
    ? `${uploadsUrl}/${profile.profileImage.replace(/^\/?(uploads\/)+/, '')}`
    : profile.profileImage || defaultProfile.profileImage;

  // Prepare cover image URL (poster / LCP — must be a real uploaded image)
  const hasCoverImage = Boolean(profile?.coverImage && profile.coverImage.trim());
  const coverImageUrl = hasCoverImage && !profile.coverImage.startsWith('/images')
    ? `${uploadsUrl}/${profile.coverImage.replace(/^\/?(uploads\/)+/, '')}`
    : hasCoverImage
      ? profile.coverImage
      : '';

  // Prepare cover video URL if available
  const hasCoverVideo = profile?.coverVideo && profile.coverVideo.trim() !== '';
  const coverVideoUrl = hasCoverVideo
    ? `${uploadsUrl}/${profile.coverVideo.replace(/^\/?(uploads\/)+/, '')}`
    : '';

  const coverImagePosition = toCoverPosition(profile?.coverImageFocal);
  const coverVideoPosition = toCoverPosition(profile?.coverVideoFocal);

  const heroStyle = !hasCoverVideo && hasCoverImage ? {
    backgroundImage: `url(${coverImageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: coverImagePosition
  } : undefined;

  // iOS Safari pauses background videos when the tab is minimized and does not
  // reliably resume them — the last frame stays frozen until play() is called.
  useEffect(() => {
    if (!hasCoverVideo) return undefined;

    const resumeCoverVideo = () => {
      if (document.visibilityState === 'hidden') return;

      const video = coverVideoRef.current;
      if (!video) return;

      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', resumeCoverVideo);
    window.addEventListener('pageshow', resumeCoverVideo);
    window.addEventListener('focus', resumeCoverVideo);

    return () => {
      document.removeEventListener('visibilitychange', resumeCoverVideo);
      window.removeEventListener('pageshow', resumeCoverVideo);
      window.removeEventListener('focus', resumeCoverVideo);
    };
  }, [hasCoverVideo, coverVideoUrl]);

  return (
    <>
      <section className={`hero ${hasCoverVideo ? 'hero-video' : ''}`} style={heroStyle}>
        {hasCoverVideo && (
          <video
            ref={coverVideoRef}
            className="hero-video-background"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={hasCoverImage ? coverImageUrl : undefined}
            style={{ objectPosition: coverVideoPosition }}
          >
            <source src={coverVideoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            {loading ? (
              <span className="text-skeleton text-skeleton--hero" aria-busy="true" />
            ) : (
              profile?.name || defaultProfile.name
            )}
          </h1>
          <p className="hero-subtitle">
            {loading ? (
              <span className="text-skeleton text-skeleton--subtitle" aria-busy="true" />
            ) : (
              profile?.title || defaultProfile.title
            )}
          </p>
          <div className="hero-cta">
            {settings.enableBookings && (
              <Link to="/bookings" className="btn btn-primary">Book a Performance</Link>
            )}
            {settings.enableLessons && (
              <Link to="/lessons" className="btn btn-secondary">Schedule a Lesson</Link>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <AdminEditableSection
            title="Edit welcome section"
            fields={[
              { name: 'welcomeTitle', label: 'Section title', path: 'home.welcomeTitle', type: 'text' }
            ]}
          >
            <h2 className="section-title animate-on-scroll">
              <DeferredPageText>{pageContent.home.welcomeTitle}</DeferredPageText>
            </h2>
          </AdminEditableSection>
          <p className="section-subtitle animate-on-scroll">
            {profile?.shortBio || defaultProfile.shortBio}
          </p>
          
          <div className="features-grid animate-stagger-group" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem',
            marginTop: '3rem'
          }}>
            <AdminEditableSection
              title="Edit Live Performances card"
              className="feature-card animate-on-scroll animate-stagger-item"
              fields={[
                { name: 'title', label: 'Title', path: 'home.features.performances.title', type: 'text' },
                { name: 'description', label: 'Description', path: 'home.features.performances.description', type: 'textarea' },
                { name: 'buttonText', label: 'Button text', path: 'home.features.performances.buttonText', type: 'text' }
              ]}
            >
              <div className="feature-card-icon">
                <FaMusic />
              </div>
              <h3><DeferredPageText>{pageContent.home.features.performances.title}</DeferredPageText></h3>
              <p className="feature-card-description">
                <DeferredPageText skeletonClassName="text-skeleton text-skeleton--paragraph">
                  {pageContent.home.features.performances.description}
                </DeferredPageText>
              </p>
              {settings.enableBookings && (
                <Link
                  to="/bookings"
                  className="btn btn-secondary"
                  aria-label={`${pageContent.home.features.performances.title} — book a live performance`}
                >
                  <DeferredPageText>{pageContent.home.features.performances.buttonText}</DeferredPageText>
                </Link>
              )}
            </AdminEditableSection>
            
            <AdminEditableSection
              title="Edit Private Lessons card"
              className="feature-card animate-on-scroll animate-stagger-item"
              fields={[
                { name: 'title', label: 'Title', path: 'home.features.lessons.title', type: 'text' },
                { name: 'description', label: 'Description', path: 'home.features.lessons.description', type: 'textarea' },
                { name: 'buttonText', label: 'Button text', path: 'home.features.lessons.buttonText', type: 'text' }
              ]}
            >
              <div className="feature-card-icon">
                <FaGraduationCap />
              </div>
              <h3><DeferredPageText>{pageContent.home.features.lessons.title}</DeferredPageText></h3>
              <p className="feature-card-description">
                <DeferredPageText skeletonClassName="text-skeleton text-skeleton--paragraph">
                  {pageContent.home.features.lessons.description}
                </DeferredPageText>
              </p>
              {settings.enableLessons && (
                <Link
                  to="/lessons"
                  className="btn btn-secondary"
                  aria-label={`${pageContent.home.features.lessons.title} — explore music lessons`}
                >
                  <DeferredPageText>{pageContent.home.features.lessons.buttonText}</DeferredPageText>
                </Link>
              )}
            </AdminEditableSection>
            
            <AdminEditableSection
              title="Edit About Me card"
              className="feature-card animate-on-scroll animate-stagger-item"
              fields={[
                { name: 'title', label: 'Title', path: 'home.features.about.title', type: 'text' },
                { name: 'description', label: 'Description', path: 'home.features.about.description', type: 'textarea' },
                { name: 'buttonText', label: 'Button text', path: 'home.features.about.buttonText', type: 'text' }
              ]}
            >
              <div className="feature-card-icon">
                <FaUser />
              </div>
              <h3><DeferredPageText>{pageContent.home.features.about.title}</DeferredPageText></h3>
              <p className="feature-card-description">
                <DeferredPageText skeletonClassName="text-skeleton text-skeleton--paragraph">
                  {pageContent.home.features.about.description}
                </DeferredPageText>
              </p>
              <Link
                to="/about"
                className="btn btn-secondary"
                aria-label={`${pageContent.home.features.about.title} — read my musical background`}
              >
                <DeferredPageText>{pageContent.home.features.about.buttonText}</DeferredPageText>
              </Link>
            </AdminEditableSection>
          </div>
        </div>
      </section>

      <FeaturedVideos />

      <section className="section bg-light">
        <div className="container">
          <AdminEditableSection
            title="Edit upcoming events section"
            fields={[
              { name: 'upcomingEventsTitle', label: 'Section title', path: 'home.upcomingEventsTitle', type: 'text' }
            ]}
          >
            <h2 className="section-title animate-on-scroll" style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <DeferredPageText>{pageContent.home.upcomingEventsTitle}</DeferredPageText>
            </h2>
          </AdminEditableSection>
          <div className="animate-on-scroll">
            <EventCalendar showFeaturedOnly={true} limit={6} />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container animate-on-scroll" style={{ textAlign: 'center' }}>
          <AdminEditableSection
            title="Edit call to action section"
            fields={[
              { name: 'ctaTitle', label: 'Section title', path: 'home.ctaTitle', type: 'text' },
              { name: 'ctaDescription', label: 'Section description', path: 'home.ctaDescription', type: 'textarea' }
            ]}
          >
            <h2 className="section-title">
              <DeferredPageText>{pageContent.home.ctaTitle}</DeferredPageText>
            </h2>
          </AdminEditableSection>
          <p style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
            <DeferredPageText skeletonClassName="text-skeleton text-skeleton--paragraph">
              {pageContent.home.ctaDescription}
            </DeferredPageText>
          </p>
          <div className="cta-buttons">
            {settings.enableBookings && (
              <Link to="/bookings" className="btn btn-primary">Book a Performance</Link>
            )}
            {settings.enableLessons && (
              <Link to="/lessons" className="btn btn-secondary">Schedule a Lesson</Link>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home; 